import React, { useState, useCallback, useRef } from 'react';
import { message as antMessage } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import BuildAppWelcome from './BuildAppWelcome';
import BuildAppChat from './BuildAppChat';
import BuildAppHistoryPanel from './BuildAppHistoryPanel';
import { buildAppChat, buildAppGenerate, buildAppSaveChat } from './buildAppApi';

/**
 * Stages:
 *   welcome       → 初始大輸入框
 *   gathering     → 多輪 AI 追問需求
 *   plan_review   → 展示 Plan 卡片（嵌入訊息流）
 *   building      → AI 生成中
 *   complete      → 生成完畢，顯示「下一步」進入 Wizard
 */

/** Fallback wizard steps when backend does not return steps[]. */
function buildFallbackSteps(result, planType) {
  const steps = [];
  if (result.eformId) steps.push('eform');
  else if (result.metaflowId) steps.push('metaflow');
  if (result.templateId) steps.push('template');
  if (result.datasetId) steps.push('dataset');
  if (planType?.includes('workflow') || result.workflowId) steps.push('workflow');
  return steps.length ? steps : ['workflow'];
}

const lbl = (t, key, fallback) => {
  const v = t(key);
  return (!v || v === key) ? fallback : v;
};

// Normalize whatever the AI returned as planType into a recognised enum key.
function normalisePlanType(raw) {
  if (!raw) return null;
  const s = raw.toLowerCase();
  if (['workflow', 'eform', 'metaflow', 'workflow+eform', 'workflow+metaflow'].includes(s)) return s;
  const hasWorkflow = s.includes('workflow') || s.includes('流程');
  const hasEform    = s.includes('eform') || s.includes('form') || s.includes('表單');
  const hasMeta     = s.includes('meta') || s.includes('metaflow');
  if (hasWorkflow && hasMeta)  return 'workflow+metaflow';
  if (hasWorkflow && hasEform) return 'workflow+eform';
  if (hasMeta)     return 'metaflow';
  if (hasEform)    return 'eform';
  if (hasWorkflow) return 'workflow';
  return null;
}

/** Debounce helper — returns a function that calls fn after delay ms. */
function useDebounce(fn, delay = 1500) {
  const timer = useRef(null);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

const BuildAppPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const [stage, setStage] = useState('welcome');
  const [messages, setMessages] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [detectedType, setDetectedType] = useState(null);
  const [planFirst, setPlanFirst] = useState(false);
  const [sending, setSending] = useState(false);
  const [planConfirming, setPlanConfirming] = useState(false);

  // ── Session state ──
  const [buildAppId, setBuildAppId] = useState(null);   // current session GUID
  const [historyOpen, setHistoryOpen] = useState(false);
  const pendingSaveMsgs = useRef([]);  // accumulate new msgs between saves

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
    // Queue for auto-save (skip building / action-only bubbles)
    if (msg.role && msg.content) {
      pendingSaveMsgs.current.push({
        role: msg.role,
        content: msg.content,
        msgType: msg.type || 'text',
        metaJson: msg.plan ? JSON.stringify(msg.plan) : null,
      });
    }
  }, []);

  // ── Auto-save (debounced) ──
  const doSave = useCallback(async (sessionId, summary, planType, planObj, channel) => {
    const msgs = [...pendingSaveMsgs.current];
    pendingSaveMsgs.current = [];
    if (!msgs.length && sessionId) return sessionId;   // nothing new to save
    try {
      const resp = await buildAppSaveChat({
        buildAppId: sessionId || null,
        name: summary ? (summary.length > 60 ? summary.slice(0, 60) + '…' : summary) : null,
        summary: summary || null,
        planType: planType || null,
        channel: channel || null,
        planJson: planObj ? JSON.stringify(planObj) : null,
        newMessages: msgs,
      });
      return resp.buildAppId || sessionId;
    } catch {
      // silent fail — save is best-effort
      return sessionId;
    }
  }, []);

  const debouncedSave = useDebounce(async (sid, summary, planType, plan, channel) => {
    const newId = await doSave(sid, summary, planType, plan, channel);
    if (newId && newId !== sid) setBuildAppId(newId);
  }, 2000);

  // Trigger save whenever new messages come in
  const triggerSave = useCallback((sid, summary, planType, plan, channel) => {
    debouncedSave(sid, summary, planType, plan, channel);
  }, [debouncedSave]);

  /* ── New App reset ── */
  const handleNewApp = useCallback(() => {
    setStage('welcome');
    setMessages([]);
    setChatHistory([]);
    setSuggestions([]);
    setDetectedType(null);
    setPlanFirst(false);
    setSending(false);
    setPlanConfirming(false);
    setBuildAppId(null);
    pendingSaveMsgs.current = [];
  }, []);

  /* ── Load from history ── */
  const handleLoadApp = useCallback((appData, chatHistoryRows) => {
    if (!appData) return;
    setBuildAppId(appData.id);
    setDetectedType(appData.planType || null);
    setPlanFirst(false);

    // Reconstruct messages from saved chat history
    const restoredMsgs = (chatHistoryRows || []).map((h) => {
      const base = { role: h.role, type: h.msgType || 'text', content: h.content };
      if (h.msgType === 'plan' && h.metaJson) {
        try { base.plan = JSON.parse(h.metaJson); } catch { /* ignore */ }
      }
      return base;
    });
    setMessages(restoredMsgs);

    // Restore raw chatHistory for next AI call
    const rawHistory = (chatHistoryRows || [])
      .filter((h) => h.role === 'user' || h.role === 'assistant')
      .map((h) => ({ role: h.role, content: h.content || '' }));
    setChatHistory(rawHistory);
    setSuggestions([]);
    pendingSaveMsgs.current = [];

    // Decide stage: complete if app has resources, else gathering
    const hasResources = appData.workflowId || appData.eformId;
    if (hasResources) {
      setStage('complete');
      // Re-inject the "enter wizard" action bubble
      const wizardSteps = appData.wizardSteps
        ? (() => { try { return JSON.parse(appData.wizardSteps); } catch { return []; } })()
        : buildFallbackSteps(appData, appData.planType);

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          type: 'complete',
          content: lbl(t, 'buildApp.completeMsg', '應用已生成！點擊下方按鈕進入建置嚮導，按步驟完善各項設定。'),
          actions: [{
            label: lbl(t, 'buildApp.wizardStart', '下一步：進入建置嚮導'),
            onClick: () => navigate('/studio/build-app/wizard', {
              state: {
                steps: wizardSteps,
                workflowId: appData.workflowId,
                eformId: appData.eformId,
                metaflowId: appData.metaflowId,
                templateId: appData.templateId,
                datasetId: appData.datasetId,
                planType: appData.planType,
                summary: appData.summary,
                buildAppId: appData.id,
                selectionsJson: appData.selectionsJson,
              },
            }),
          }],
        },
      ]);
    } else {
      setStage(restoredMsgs.length > 0 ? 'gathering' : 'welcome');
    }

    antMessage.success('已載入應用：' + (appData.name || '未命名'));
  }, [t, navigate]);

  /* ── Modify Plan → back to gathering ── */
  const handleModifyPlan = useCallback(() => {
    setStage('gathering');
    appendMessage({
      role: 'assistant',
      type: 'text',
      content: lbl(t, 'buildApp.modifyPlanPrompt', '好的，請告訴我你想調整哪些部分？'),
    });
    setSuggestions([
      lbl(t, 'buildApp.suggChangeChannel', '更換渠道'),
      lbl(t, 'buildApp.suggAddApproval', '加入審批步驟'),
      lbl(t, 'buildApp.suggRemoveForm', '不需要表單'),
    ]);
  }, [t, appendMessage]);

  /* ── Run Generate ── */
  const runGenerate = useCallback(async (plan) => {
    setStage('building');
    setSuggestions([]);
    appendMessage({
      role: 'assistant',
      type: 'building',
      content: lbl(t, 'buildApp.buildingMsg', 'AI 正在生成應用，請稍候…'),
      progress: null,
    });

    try {
      const normalisedPlan = plan ? { ...plan, planType: normalisePlanType(plan.planType) || plan.planType } : {};
      const result = await buildAppGenerate({
        ...normalisedPlan,
        buildAppId: buildAppId || undefined,
        planJson: JSON.stringify(plan),
      });
      const resolvedType = normalisePlanType(result.planType) || normalisePlanType(normalisedPlan.planType);
      setDetectedType(resolvedType || null);

      // Update session id from backend response
      if (result.buildAppId) setBuildAppId(result.buildAppId);

      setStage('complete');

      const wizardSteps = result.steps && result.steps.length > 0
        ? result.steps
        : buildFallbackSteps(result, resolvedType);

      appendMessage({
        role: 'assistant',
        type: 'complete',
        content: lbl(t, 'buildApp.completeMsg', '應用已生成！點擊下方按鈕進入建置嚮導，按步驟完善各項設定。'),
        actions: [
          {
            label: lbl(t, 'buildApp.wizardStart', '下一步：進入建置嚮導'),
            onClick: () => navigate('/studio/build-app/wizard', {
              state: {
                steps: wizardSteps,
                workflowId: result.workflowId,
                eformId: result.eformId,
                metaflowId: result.metaflowId,
                templateId: result.templateId,
                datasetId: result.datasetId,
                planType: resolvedType,
                summary: normalisedPlan.summary,
                buildAppId: result.buildAppId,
              },
            }),
          },
        ],
      });
    } catch (err) {
      antMessage.error(err.message || '生成失敗，請重試');
      setStage('gathering');
      appendMessage({
        role: 'assistant',
        type: 'text',
        content: lbl(t, 'buildApp.generateFailMsg', '生成過程中發生錯誤，請描述需求後重試。'),
      });
    }
  }, [t, appendMessage, navigate, buildAppId]);

  /* ── Confirm Plan ── */
  const handleConfirmPlan = useCallback(async (plan) => {
    setPlanConfirming(true);
    await runGenerate(plan);
    setPlanConfirming(false);
  }, [runGenerate]);

  /* ── Process API response ── */
  const processApiResponse = useCallback(async (res, historyAfterUserMsg, pf, summary) => {
    const aiMsg = { role: 'assistant', type: 'text', content: res.reply };
    appendMessage(aiMsg);
    const nextHistory = [...historyAfterUserMsg, { role: 'assistant', content: res.reply }];
    setChatHistory(nextHistory);
    setSuggestions(res.suggestions || []);
    if (res.detectedType) setDetectedType(res.detectedType);

    const effectivePlan = res.plan
      ? { ...res.plan, planType: res.detectedType || res.plan.planType }
      : res.plan;

    // Trigger auto-save after AI reply
    triggerSave(
      buildAppId,
      effectivePlan?.summary || summary,
      effectivePlan?.planType || res.detectedType,
      effectivePlan,
      effectivePlan?.channel,
    );

    if (res.readyToBuild && pf && effectivePlan) {
      setStage('plan_review');
      appendMessage({ role: 'assistant', type: 'plan', plan: effectivePlan });
    } else if (res.readyToBuild && !pf) {
      await runGenerate(effectivePlan);
    }
  }, [appendMessage, runGenerate, triggerSave, buildAppId]);

  /* ── Welcome → First send ── */
  const handleStart = useCallback(async ({ userMessage, planFirst: pf }) => {
    setPlanFirst(pf);
    setStage('gathering');

    const userMsg = { role: 'user', type: 'text', content: userMessage };
    setMessages([userMsg]);
    const history = [{ role: 'user', content: userMessage }];
    setChatHistory(history);
    // Queue first user message
    pendingSaveMsgs.current = [{ role: 'user', content: userMessage, msgType: 'text', metaJson: null }];
    setSending(true);

    try {
      const res = await buildAppChat({
        userMessage,
        chatHistory: [],
        planFirst: pf,
        imageBase64: null,
        imageType: null,
        uiLanguage: currentLanguage,
      });
      await processApiResponse(res, history, pf, userMessage);
    } catch (err) {
      antMessage.error(err.message || '對話請求失敗');
      setStage('welcome');
      setMessages([]);
    } finally {
      setSending(false);
    }
  }, [processApiResponse]);

  /* ── Chat send ── */
  const handleSend = useCallback(async ({ userMessage, imageBase64, imageType }) => {
    if (sending) return;

    const userMsg = {
      role: 'user',
      type: 'text',
      content: userMessage,
      imagePreview: imageBase64 ? `data:${imageType};base64,${imageBase64}` : null,
    };
    appendMessage(userMsg);
    const newHistory = [...chatHistory, { role: 'user', content: userMessage }];
    setChatHistory(newHistory);
    setSuggestions([]);
    setSending(true);

    try {
      const res = await buildAppChat({
        userMessage,
        chatHistory,
        planFirst,
        imageBase64: imageBase64 || null,
        imageType: imageType || null,
        uiLanguage: currentLanguage,
      });
      await processApiResponse(res, newHistory, planFirst, userMessage);
    } catch (err) {
      antMessage.error(err.message || '對話請求失敗');
    } finally {
      setSending(false);
    }
  }, [sending, chatHistory, planFirst, appendMessage, processApiResponse]);

  /* ── Suggestion chip click ── */
  const handleSuggestion = useCallback((text) => {
    handleSend({ userMessage: text, imageBase64: null, imageType: null });
  }, [handleSend]);

  return (
    <div style={styles.page}>
      {stage === 'welcome' ? (
        <BuildAppWelcome
          onStart={handleStart}
          loading={sending}
          onOpenHistory={() => setHistoryOpen(true)}
        />
      ) : (
        <BuildAppChat
          messages={messages}
          suggestions={suggestions}
          detectedType={detectedType}
          stage={stage}
          sending={sending}
          planConfirming={planConfirming}
          onSend={handleSend}
          onSuggestion={handleSuggestion}
          onConfirmPlan={handleConfirmPlan}
          onModifyPlan={handleModifyPlan}
          onNewApp={handleNewApp}
          onOpenHistory={() => setHistoryOpen(true)}
        />
      )}

      <BuildAppHistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onLoadApp={handleLoadApp}
      />
    </div>
  );
};

const styles = {
  page: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
};

export default BuildAppPage;
